import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type DragEvent,
  type KeyboardEvent,
} from "react";
import { Button, Select } from "@/components/ui";
import { PAGES, type AttachmentOut } from "@/api/types/studio";
import { useSendMessage, uploadStudioAttachment } from "@/api/hooks/useStudio";
import { isApiError } from "@/api/client";
import { canSend, MAX_ATTACHMENTS, type ComposerState } from "./compose";
import { isAttachable, isImageFile, prepareUpload } from "./prepareUpload";

/** Uploads in flight per composer; the rest wait in a queue. */
const MAX_CONCURRENT_UPLOADS = 3;
const TEXTAREA_MAX_PX = 160;

interface PendingFile {
  key: string;
  filename: string;
  /** Object URL for an image thumbnail; released when the chip goes away. */
  previewUrl?: string;
  progress: number;
  attachment?: AttachmentOut;
  error?: string;
}

interface UploadJob {
  key: string;
  file: File;
}

let chipSeq = 0;
function nextChipKey(): string {
  chipSeq += 1;
  return `chip-${chipSeq}`;
}

/** Server `detail` when the API gave one, otherwise a plain fallback. */
function failureMessage(err: unknown): string {
  if (isApiError(err)) {
    const detail = err.response?.data?.detail;
    return typeof detail === "string" && detail ? detail : "Upload failed";
  }
  // Local preparation errors (e.g. an unreadable HEIC) carry their own message.
  if (err instanceof Error && err.message) return err.message;
  return "Upload failed";
}

function isCoarsePointer(): boolean {
  // Phones/tablets: Enter must insert a newline; the send button sends.
  return typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches;
}

function hasFiles(e: DragEvent<HTMLElement>): boolean {
  return Array.from(e.dataTransfer.types).includes("Files");
}

function Thumb({ src, alt }: { src: string; alt: string }) {
  const [broken, setBroken] = useState(false);
  // HEIC previews are blank in browsers without HEIC support; show a glyph instead.
  if (broken) {
    return (
      <span
        aria-hidden="true"
        className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md bg-bg-muted text-text-muted"
      >
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.16-5.16a2.25 2.25 0 013.18 0l5.41 5.41m-1.5-1.5l1.41-1.41a2.25 2.25 0 013.18 0l2.65 2.66M3.75 4.5h16.5a1.5 1.5 0 011.5 1.5v12a1.5 1.5 0 01-1.5 1.5H3.75a1.5 1.5 0 01-1.5-1.5V6a1.5 1.5 0 011.5-1.5z" />
        </svg>
      </span>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      onError={() => setBroken(true)}
      className="h-14 w-14 shrink-0 rounded-md object-cover"
    />
  );
}

/**
 * Thumb-reachable composer: auto-growing textarea, Chat/Feedback mode
 * toggle (page picker in Feedback), multi-file attach with per-file
 * upload progress, drag-and-drop and paste, Enter-to-send on desktop
 * (Shift+Enter = newline).
 */
export function Composer() {
  const [body, setBody] = useState("");
  const [kind, setKind] = useState<"chat" | "feedback">("chat");
  const [page, setPage] = useState<string>("");
  const [pending, setPending] = useState<PendingFile[]>([]);
  const [capNotice, setCapNotice] = useState(false);
  const [dragging, setDragging] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragDepthRef = useRef(0);
  // Only ever mutated in place (push/shift/splice), never reassigned, so the
  // unmount cleanup can hold on to the same array.
  const queueRef = useRef<UploadJob[]>([]);
  const activeRef = useRef(0);
  const objectUrlsRef = useRef(new Set<string>());
  const sendMessage = useSendMessage();

  // Auto-grow: expand to content, capped so the composer never eats the thread.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, TEXTAREA_MAX_PX)}px`;
  }, [body]);

  // On unmount: drop queued uploads and release any thumbnails still alive.
  useEffect(() => {
    const urls = objectUrlsRef.current;
    const queue = queueRef.current;
    return () => {
      queue.splice(0);
      for (const url of urls) URL.revokeObjectURL(url);
      urls.clear();
    };
  }, []);

  const uploading = pending.some((p) => !p.attachment && !p.error);
  const attachmentCount = pending.filter((p) => p.attachment).length;
  const state: ComposerState = { body, kind, page: page || null, uploading, attachmentCount };
  const ready = canSend(state) && !sendMessage.isPending;

  function patchChip(key: string, patch: Partial<PendingFile>) {
    setPending((prev) => prev.map((p) => (p.key === key ? { ...p, ...patch } : p)));
  }

  async function runUpload({ key, file }: UploadJob) {
    try {
      const prepared = await prepareUpload(file);
      const attachment = await uploadStudioAttachment(prepared, (percent) =>
        patchChip(key, { progress: percent }),
      );
      patchChip(key, { attachment, progress: 100 });
    } catch (err) {
      patchChip(key, { error: failureMessage(err) });
    }
  }

  /** Start queued uploads until MAX_CONCURRENT_UPLOADS are running. */
  function pump() {
    while (activeRef.current < MAX_CONCURRENT_UPLOADS) {
      const job = queueRef.current.shift();
      if (!job) return;
      activeRef.current += 1;
      void runUpload(job).finally(() => {
        activeRef.current -= 1;
        pump();
      });
    }
  }

  function makePreview(file: File): string | undefined {
    if (!isImageFile(file)) return undefined;
    const url = URL.createObjectURL(file);
    objectUrlsRef.current.add(url);
    return url;
  }

  function releasePreview(url: string | undefined) {
    if (!url) return;
    objectUrlsRef.current.delete(url);
    URL.revokeObjectURL(url);
  }

  function addFiles(files: File[]) {
    if (files.length === 0) return;

    // The cap counts photos that are uploading or uploaded; failed chips do not count.
    let counted = pending.filter((p) => !p.error).length;
    let overflow = false;
    const chips: PendingFile[] = [];
    const jobs: UploadJob[] = [];

    for (const file of files) {
      const key = nextChipKey();
      if (!isAttachable(file)) {
        chips.push({ key, filename: file.name, progress: 0, error: "Only photos and PDFs can be attached" });
        continue;
      }
      if (counted >= MAX_ATTACHMENTS) {
        overflow = true;
        continue;
      }
      counted += 1;
      chips.push({ key, filename: file.name, progress: 0, previewUrl: makePreview(file) });
      jobs.push({ key, file });
    }

    setPending((prev) => [...prev, ...chips]);
    setCapNotice(overflow);
    queueRef.current.push(...jobs);
    pump();
  }

  function send() {
    if (!ready) return;
    const sent = pending.flatMap((p) => (p.attachment ? [p.attachment] : []));
    const sentKeys = new Set(pending.filter((p) => p.attachment).map((p) => p.key));
    const failedKeys = new Set(pending.filter((p) => p.error).map((p) => p.key));

    sendMessage.mutate(
      {
        body: body.trim(),
        kind,
        page: kind === "feedback" ? page : null,
        attachment_ids: sent.map((a) => a.id),
        attachments: sent,
      },
      {
        onSuccess: () => {
          for (const p of pending) {
            if (sentKeys.has(p.key) || failedKeys.has(p.key)) releasePreview(p.previewUrl);
          }
          setBody("");
          setPage("");
          setCapNotice(false);
          // Sent chips leave. Failed chips stay, without thumbnails, until removed.
          setPending((prev) =>
            prev
              .filter((p) => !sentKeys.has(p.key))
              .map((p) => (failedKeys.has(p.key) ? { ...p, previewUrl: undefined } : p)),
          );
        },
      },
    );
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key !== "Enter" || e.shiftKey || isCoarsePointer()) return;
    e.preventDefault();
    send();
  }

  function handleFilesSelected(e: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    addFiles(files);
  }

  function handlePaste(e: ClipboardEvent<HTMLTextAreaElement>) {
    // Plain-text pastes have no files and fall through to the textarea.
    const files = Array.from(e.clipboardData.files);
    if (files.length === 0) return;
    addFiles(files);
  }

  function handleDragEnter(e: DragEvent<HTMLDivElement>) {
    if (!hasFiles(e)) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    setDragging(true);
  }

  function handleDragOver(e: DragEvent<HTMLDivElement>) {
    if (!hasFiles(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }

  function handleDragLeave() {
    // The depth only counts file drags (see handleDragEnter), so no type check here.
    if (dragDepthRef.current === 0) return;
    dragDepthRef.current -= 1;
    if (dragDepthRef.current === 0) setDragging(false);
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    if (!hasFiles(e)) return;
    e.preventDefault();
    dragDepthRef.current = 0;
    setDragging(false);
    addFiles(Array.from(e.dataTransfer.files));
  }

  function removePending(key: string) {
    const queue = queueRef.current;
    const queued = queue.findIndex((j) => j.key === key);
    if (queued >= 0) queue.splice(queued, 1);
    releasePreview(pending.find((p) => p.key === key)?.previewUrl);
    setPending((prev) => prev.filter((p) => p.key !== key));
  }

  const modeButtonClass = (active: boolean) =>
    `rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
      active ? "bg-copper text-white shadow-xs" : "text-text-secondary hover:text-text-primary"
    }`;

  return (
    <div
      data-testid="studio-composer"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`relative border-t border-border bg-bg-card px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2.5 transition-colors ${
        dragging ? "bg-copper-light/40 ring-2 ring-inset ring-copper" : ""
      }`}
    >
      {dragging && (
        <div className="pointer-events-none absolute inset-2 z-10 flex items-center justify-center rounded-lg border-2 border-dashed border-copper bg-bg-card/90 text-sm font-semibold text-copper">
          Drop photos or PDFs to attach
        </div>
      )}

      {capNotice && (
        <p role="status" className="mb-2 text-xs text-warning">
          {`Up to ${MAX_ATTACHMENTS} photos per message. Send these, then add the rest.`}
        </p>
      )}

      {pending.length > 0 && (
        // Capped and scrollable so a big batch of photos never pushes the thread off a phone screen.
        <div data-testid="studio-pending-list" className="mb-2 flex max-h-44 flex-wrap gap-2 overflow-y-auto sm:max-h-60">
          {pending.map((p) => (
            <div
              key={p.key}
              data-testid="studio-pending-chip"
              className="flex min-w-0 max-w-full items-center gap-2 rounded-md border border-border bg-bg-body p-1.5 text-xs text-text-primary"
            >
              {p.previewUrl ? (
                <Thumb src={p.previewUrl} alt={p.filename} />
              ) : (
                <svg className="ml-1 h-4 w-4 shrink-0 text-danger" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m6.75 12l-3.75 3.75m0 0l-3.75-3.75M12 18.75V9" />
                </svg>
              )}
              <div className="flex min-w-0 max-w-[11rem] flex-col gap-0.5">
                <span className="truncate font-medium">{p.filename}</span>
                {p.attachment ? (
                  <svg
                    role="img"
                    aria-label="Uploaded"
                    className="h-4 w-4 text-success"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                ) : p.error ? (
                  <>
                    <span className="font-semibold text-danger">Failed</span>
                    <span className="break-words text-danger">{p.error}</span>
                  </>
                ) : (
                  <span className="text-text-muted">{p.progress}%</span>
                )}
              </div>
              <button
                type="button"
                onClick={() => removePending(p.key)}
                className="shrink-0 self-start text-text-muted hover:text-danger"
                aria-label={`Remove ${p.filename}`}
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {kind === "feedback" && (
        <Select
          aria-label="Site page"
          value={page}
          onChange={(e) => setPage(e.target.value)}
          className="mb-2 h-9"
          data-testid="studio-page-select"
        >
          <option value="">Which page is this about?</option>
          {PAGES.map((p) => (
            <option key={p.path} value={p.path}>
              {p.label}
            </option>
          ))}
        </Select>
      )}

      <div className="flex items-end gap-2">
        <div className="flex items-center gap-0.5 rounded-lg bg-bg-muted p-0.5">
          <button type="button" className={modeButtonClass(kind === "chat")} aria-pressed={kind === "chat"} onClick={() => setKind("chat")}>
            Chat
          </button>
          <button type="button" className={modeButtonClass(kind === "feedback")} aria-pressed={kind === "feedback"} onClick={() => setKind("feedback")}>
            Feedback
          </button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf"
          multiple
          className="hidden"
          onChange={handleFilesSelected}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-text-secondary hover:bg-bg-hover hover:text-text-primary"
          aria-label="Attach images or PDFs"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" />
          </svg>
        </button>

        <textarea
          ref={textareaRef}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={kind === "feedback" ? "What should change?" : "Message the assistant…"}
          rows={1}
          aria-label="Message"
          data-testid="studio-composer-input"
          className="max-h-40 min-h-0 flex-1 resize-none rounded-md border border-border bg-bg-body px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-primary focus:ring-[3px] focus:ring-primary/15"
        />

        <Button
          size="md"
          onClick={send}
          disabled={!ready}
          data-testid="studio-send"
          aria-label="Send message"
          className="h-10 w-10 shrink-0 px-0"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
          </svg>
        </Button>
      </div>
    </div>
  );
}
